import { type } from 'arktype';

export const PERSON_DELETION_OPTION_KEYS = [
  'deleteLinkedUser',
  'removeTeamAssignments',
  'removeLocationAssignments',
  'clearSkillAssignments',
  'removeFuturePlanInstanceAssignments',
] as const;

export type PersonDeletionOptionKey = (typeof PERSON_DELETION_OPTION_KEYS)[number];

export const personDeletionOptionsSchema = type({
  deleteLinkedUser: type('boolean').configure({
    description: 'ON permanently deletes the DB-resolved linked account; OFF retains and unlinks it.',
  }),
  removeTeamAssignments: type('boolean').configure({ description: 'ON deletes all target-person team junctions including history.' }),
  removeLocationAssignments: type('boolean').configure({
    description: 'ON deletes all target-person location junctions including history.',
  }),
  clearSkillAssignments: type('boolean').configure({
    description: 'ON deletes all target-person skill assignment junctions including history.',
  }),
  removeFuturePlanInstanceAssignments: type('boolean').configure({
    description: 'ON removes future-dated plan-instance assignments strictly after server UTC today.',
  }),
})
  // ArkType preserves undeclared keys by default, so unknown flags must be rejected explicitly.
  .onUndeclaredKey('reject')
  .configure({
    description: 'Explicit person-deletion options. All five flags are required as actual booleans.',
    examples: [
      {
        deleteLinkedUser: true,
        removeTeamAssignments: true,
        removeLocationAssignments: true,
        clearSkillAssignments: true,
        removeFuturePlanInstanceAssignments: true,
      },
    ],
  });

export type PersonDeletionOptions = typeof personDeletionOptionsSchema.t;

export function isExplicitDeletionOptions(body: unknown): body is PersonDeletionOptions {
  return !(personDeletionOptionsSchema(body) instanceof type.errors);
}

// Single ratified transport: separate additive POST command. DELETE with a body has no app-plane precedent and risks proxy stripping, and query flags arrive as strings; POST JSON preserves explicit false through the generator while bare DELETE stays untouched.
export const PERSON_DELETION_COMMAND_ROUTE = 'POST /api/v1/core/persons/:id/delete' as const;

export const PERSON_DELETION_LEGACY_ROUTE = 'DELETE /api/v1/core/persons/:id' as const;

export const PERSON_DELETION_STATUSES = {
  success: 204,
  invalidOptions: 400,
  forbidden: 403,
  personNotFound: 404,
} as const;

export type PersonDeletionStatus = (typeof PERSON_DELETION_STATUSES)[keyof typeof PERSON_DELETION_STATUSES];

export type PersonDeletionOutcome = { outcome: 'deleted'; id: string } | { outcome: 'personNotFound'; id: string };
